import { Font } from 'mm-theme-configuration';
import { WEB_FONTS_PROVIDERS } from './webFontsProviders';
import { weightNameTransformer } from './webfontName.adapter';
import { baseGoogleFontsUrl, baseCustomFontsUrl } from '../../utils/uri.utils';

interface ParsedVariant {
    weight: string;
    style: string;
    format?: string;
}

interface FontStyles {
    [key: string]: string;
}

const webFontFormats = {
  woff: 'woff',
  woff2: 'woff2',
  eot: 'eot',
};

const defaultFormat = webFontFormats.woff;

export const createFontFamilyNameParam = (familyName: string) => {
  return familyName.split(' ').join('+');
};

export const parseVariant: (variant: string) => ParsedVariant = (variant = '400') => {
  const match = variant.match(/^([0-9]*)([a-z])$/i);
  if (match) {
    return {
      weight: match[1],
      style: match[2],
    };
  }
  return {
    weight: variant,
    style: 'n',
  };
};

export const createFVDKey = (family: string, variant: string) => {
  const { weight, style } = parseVariant(variant);

  return `wf-${family.replace(/\s/g, '').toLowerCase()}-${style[0] + weight[0]}`;
};

export const getStyle = (key = 'n', transform = (style: string) => style) => {
  const fontStyles: FontStyles = {
    i: 'italic',
    n: 'normal',
  };

  return transform(fontStyles[key]);
};

export const createGoogleFontsParams = (fontConfig: Font) => {
  return fontConfig.variants.reduce(
    (acc: string, variant: string, index: number) => {
      const divider = index === 0 ? ':' : ',';

      return `${acc}${divider}${variant}`;
    },
    createFontFamilyNameParam(fontConfig.family),
  );
};

export const createGoogleFontsUrl = (fontConfig: Font) => {
  return `${baseGoogleFontsUrl}=${createGoogleFontsParams(fontConfig)}`;
};

export const createFontFamilyDeclaration = ({ family, weight, style, format = defaultFormat }: ParsedVariant & Font) => {
  const fontStyle = style !== 'n' ? getStyle(style, s => s.charAt(0).toUpperCase() + s.slice(1)) : '';
  const localName = `${family}-${weightNameTransformer(weight, true)[0].replace(/\s/g, '')}${fontStyle}`;

  return `
  @font-face {font-family: '${family}';
  font-style: ${getStyle(style)};
  font-weight: ${weightNameTransformer(weight)};
  src: local('${family} ${weight} ${style}'), local('${localName}'),url(${baseCustomFontsUrl()}/${family.toLowerCase()}/${format}/${localName}.${format}) format('${format}');
  }`;
};

export const createCustomStylesheetDataUri = (fontConfig: Font) => {
  const cssSource = fontConfig.variants.reduce((css: string, variant: string) => {
    const styleAndWeight = parseVariant(variant);
    const fontFaceDeclaration = createFontFamilyDeclaration({
      ...fontConfig,
      ...styleAndWeight,
    });
    return `${css}${fontFaceDeclaration}`;
  }, '');

  return `data:text/css;base64,${Buffer.from(cssSource).toString('base64')}`;
};

export const createCustomStylesheet = (fontConfig: Font) => {
  return fontConfig.stylesheetUrl || createCustomStylesheetDataUri(fontConfig);
};

export const getStylesheetByProvider = (fontConfig: Font) => {
  switch (fontConfig.provider) {
    case WEB_FONTS_PROVIDERS.GOOGLE:
      return createGoogleFontsUrl(fontConfig);
    case WEB_FONTS_PROVIDERS.CUSTOM:
      return createCustomStylesheet(fontConfig);
    default:
      return createGoogleFontsUrl(fontConfig);
  }
};
